/**
 * Sets a JS-managed `--app-height` CSS custom property on the document root,
 * solving the iOS Safari `100vh` bug (100vh includes the bottom toolbar, so a
 * full-height fixed element overflows behind it). Consumed by CSS as
 * `height: var(--app-height, 100vh)`. Needed because dynamic viewport units
 * (`dvh`) require Safari 15.4+, above the project's Safari 14 floor.
 * @module appHeight
 */

let ticking = false;

function setAppHeight(): void {
  // Coalesce bursts into one write per frame. iOS Safari fires
  // `visualViewport.resize` every frame during address-bar show/hide and
  // during pinch-zoom; without this guard each event would force a synchronous
  // `:root` style write (and a wasted write of an unchanged value during a
  // pinch, since innerHeight is unaffected by pinch).
  if (ticking) {
    return;
  }
  ticking = true;
  requestAnimationFrame(() => {
    ticking = false;
    const next = `${String(window.innerHeight)}px`;
    const root = document.documentElement;
    if (root.style.getPropertyValue("--app-height") !== next) {
      root.style.setProperty("--app-height", next);
    }
  });
}

/**
 * Sets `--app-height` immediately and keeps it in sync with the viewport.
 * Intended to be called once, globally. Not wrapped in try/catch — the caller
 * (globalEntry) wraps it in `safeCall`.
 */
export function initAppHeight(): void {
  setAppHeight();

  // visualViewport.resize is more reliable than window.resize on iOS Safari
  // during address-bar show/hide animations. Explicit branch (not a `??`
  // union) keeps strict-mode TS overload resolution happy.
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", setAppHeight, {
      passive: true,
    });
  } else {
    window.addEventListener("resize", setAppHeight, { passive: true });
  }

  window.addEventListener("orientationchange", setAppHeight, { passive: true });
}
